import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articleContents } from '@/mocks/categories';
import type { ArticleContent } from '@/mocks/categories';

function ArticleNotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 px-4">
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-accent-100">
          <i className="ri-error-warning-line text-3xl text-accent-600"></i>
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Artigo Nao Encontrado</h1>
        <p className="text-foreground-500 text-sm mb-6">O artigo que voce procura pode ter sido removido ou o link esta incorreto.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Voltar ao Inicio
        </Link>
      </div>
    </div>
  );
}

function ArticleJsonLd({ article, siteUrl }: { article: ArticleContent; siteUrl: string }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    author: {
      '@type': 'Organization',
      name: 'HoobuySpreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'HoobuySpreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/artigo/${article.slug}`,
    },
    inLanguage: 'pt-BR',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

function renderSection(section: ArticleContent['sections'][0], idx: number) {
  switch (section.type) {
    case 'text':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          {section.body?.split('\n').filter(Boolean).map((p, pi) => (
            <p key={pi} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">{p}</p>
          ))}
        </section>
      );

    case 'benefits':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.items?.map((item, ii) => (
              <div key={ii} className="bg-background-100 rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className={`${item.icon} text-lg text-primary-600`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case 'steps':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          <div className="space-y-6">
            {section.items?.map((item, ii) => (
              <div key={ii} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 font-bold text-xs">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground-900 mb-1.5">{item.title}</h3>
                  <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case 'comparison':
    case 'table':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          {section.body && (
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6">{section.body}</p>
          )}
          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-background-100">
                  {section.tableHeaders?.map((h) => (
                    <th key={h} className="px-4 md:px-6 py-4 text-sm font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {section.tableRows?.map((row, ri) => (
                  <tr key={ri} className={`transition-colors ${row.highlight ? 'bg-primary-50/50 hover:bg-primary-50' : 'hover:bg-background-50'}`}>
                    {row.cells.map((cell, ci) => (
                      <td key={ci} className={`px-4 md:px-6 py-4 text-sm whitespace-nowrap ${ci === 0 && row.highlight ? 'font-semibold text-primary-600' : 'text-foreground-700'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );

    case 'tips':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">{section.heading}</h2>
          {section.body && (
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">{section.body}</p>
          )}
          <ul className="space-y-3">
            {section.items?.map((item, ii) => (
              <li key={ii} className="flex gap-3 text-foreground-700 text-sm leading-relaxed">
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-[10px] mt-0.5">
                  <i className="ri-shield-check-fill"></i>
                </span>
                {item.tip}
              </li>
            ))}
          </ul>
        </section>
      );

    case 'faq':
      return (
        <section key={idx} className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">{section.heading}</h2>
          <div className="space-y-3">
            {section.items?.map((item, ii) => (
              <details key={ii} className="group bg-background-100 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                  <span className="text-sm font-semibold text-foreground-800 pr-4">{item.q}</span>
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 transition-colors">
                    <i className="ri-arrow-down-s-line text-foreground-600 group-open:text-primary-600 text-sm transition-transform duration-200 group-open:rotate-180"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section key={idx} className="bg-primary-500/10 rounded-2xl p-8 md:p-10 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">{section.heading}</h2>
          {section.body && (
            <p className="text-foreground-600 text-sm md:text-base mb-6 max-w-lg mx-auto">{section.body}</p>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-bold text-sm rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Acessar HoobuySpreadsheet
            </a>
            <Link
              to="/"
              className="px-8 py-3.5 bg-foreground-950 text-background-50 font-bold text-sm rounded-full hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Voltar ao Inicio
            </Link>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const article = articleContents.find((a) => a.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.cfd';

  if (!article) {
    return <ArticleNotFound />;
  }

  return (
    <main className="min-h-screen">
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full min-h-[350px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.heroImage}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/35 to-black/25"></div>

        <div className="relative z-10 w-full px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-accent-500 text-background-50 dark:text-foreground-950 rounded-full whitespace-nowrap">
                <i className="ri-fire-fill text-[10px]"></i>
                {article.badge}
              </span>
              <span className="text-background-50/60 text-xs whitespace-nowrap">{article.readTime} de leitura</span>
              <span className="text-background-50/60 text-xs whitespace-nowrap">{article.date}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 drop-shadow-md">
              {article.title}
            </h1>
            <p className="text-background-50/75 text-sm md:text-base max-w-2xl">
              {article.description}
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Inicio</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-700 font-medium whitespace-nowrap">{article.title}</span>
          </nav>

          {/* Dynamic Sections */}
          {article.sections.map((section, idx) => renderSection(section, idx))}

          {/* Internal Link Navigation */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-xs text-foreground-500 whitespace-nowrap">Navegacao:</span>
              <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap border-b border-primary-300 hover:border-primary-500">
                ← Pagina Inicial
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap border-b border-transparent hover:border-foreground-300 cursor-pointer"
              >
                Kaspreadsheet
              </a>
            </div>
          </div>
        </div>
      </article>

      <ArticleJsonLd article={article} siteUrl={siteUrl} />

      <Footer />
    </main>
  );
}